import React, { useEffect, useMemo, useState } from "react";
import { Pagination } from "@instructure/ui-pagination";
import { Table } from "@instructure/ui-table";
import { Responsive } from "@instructure/ui-responsive";
import { ScreenReaderContent } from "@instructure/ui-a11y-content";
import { DTI_REFRESH_EMPTY_MESSAGE } from "./DtiRefreshLabel";

function getRowKey(row, page, rowIndex) {
  const parts = [
    page,
    rowIndex,
    row.id,
    row.user_id,
    row.user_name,
    row.title,
    row.course_name,
    row.subject,
    row.grade,
  ].filter((part) => part !== undefined && part !== null && part !== "");

  return parts.length > 1 ? parts.join("-") : `row-${page}-${rowIndex}`;
}

export default function PaginatedTable({
  headers = [],
  rows = [],
  perPage = 10,
  layout,
  emptyMessage,
}) {
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState(headers[0]?.id);
  const [ascending, setAscending] = useState(true);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  useEffect(() => {
    if (headers[0]?.id) {
      setSortBy(headers[0].id);
      setAscending(true);
      setPage(0);
    }
  }, [headers]);

  const sortedRows = useMemo(() => {
    if (!sortBy) return [...rows];

    const next = [...rows].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });

    return ascending ? next : next.reverse();
  }, [rows, sortBy, ascending]);

  const pageSize = Number(perPage) > 0 ? Number(perPage) : 10;
  const pageCount = Math.ceil(sortedRows.length / pageSize);
  const currentPage = pageCount > 0 ? Math.min(page, pageCount - 1) : 0;

  const startIndex = currentPage * pageSize;
  const slicedRows = sortedRows.slice(startIndex, startIndex + pageSize);
  const direction = ascending ? "ascending" : "descending";

  const handleSort = (event, { id }) => {
    if (id === sortBy) {
      setAscending((value) => !value);
    } else {
      setSortBy(id);
      setAscending(true);
    }
    setPage(0);
  };

  if (!rows.length) {
    return (
      <div className="dti-empty dti-detail-table-empty">
        <strong>Chưa có dữ liệu</strong>
        {emptyMessage || DTI_REFRESH_EMPTY_MESSAGE}
      </div>
    );
  }

  return (
    <div className="dti-paginated-table">
      <Responsive
        query={{
          small: { maxWidth: "40rem" },
          large: { minWidth: "41rem" },
        }}
        props={{
          small: { layout: "stacked" },
          large: { layout: "auto" },
        }}
      >
        {(responsiveProps) => (
          <Table key={`dti-table-page-${currentPage}`} margin="auto" {...responsiveProps}>
            <Table.Head
              renderSortLabel={<ScreenReaderContent>Sắp xếp theo</ScreenReaderContent>}
            >
              <Table.Row>
                {headers.map(({ id, text, width }) => (
                  <Table.ColHeader
                    key={id}
                    id={id}
                    width={width}
                    onRequestSort={handleSort}
                    sortDirection={id === sortBy ? direction : "none"}
                  >
                    {text}
                  </Table.ColHeader>
                ))}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {slicedRows.map((row, rowIndex) => (
                <Table.Row key={getRowKey(row, currentPage, rowIndex)}>
                  {headers.map(({ id, renderCell, textAlign }) => (
                    <Table.Cell
                      key={id}
                      textAlign={
                        layout === "stacked" || responsiveProps.layout === "stacked"
                          ? "start"
                          : textAlign
                      }
                    >
                      {renderCell ? renderCell(row[id]) : row[id]}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Responsive>

      {pageCount > 1 && (
        <Pagination
          variant="compact"
          labelNext="Tiếp theo"
          labelPrev="Trước"
          margin="large"
        >
          {Array.from({ length: pageCount }, (_, index) => (
            <Pagination.Page
              key={`page-btn-${index}`}
              onClick={() => setPage(index)}
              current={index === currentPage}
            >
              {index + 1}
            </Pagination.Page>
          ))}
        </Pagination>
      )}
    </div>
  );
}
